import { jsx as _jsx } from "react/jsx-runtime";
import { useRender } from '@a-type/ui';
import { createContext, useContext, useEffect, useRef, useState, } from 'react';
import { useMergedRef } from '../hooks/useMergedRef.js';
import { useBindBounds } from './bounds.js';
import { draggableDataRegistry, droppableDataRegistry, } from './dataRegistry.js';
import { useDndStore } from './dndStore.js';
import { gesture } from './gestureStore.js';
import { dndLogger } from './logger.js';
import { useDroppable, } from './useDroppable.js';
export function Droppable({ id, onDrop, onOver, disabled, ref: userRef, accept, onReject, render, tags, data, noParenting, priority, svg, ...rest }) {
    const { isAcceptedOver, isRejectedOver } = useDroppable({
        onDrop,
        onOver,
        accept,
        onReject,
        id,
        disabled,
        tags,
    });
    const bindBounds = useBindBounds(id, priority);
    const [localEl, localRef] = useState(null);
    const acceptStable = useRef(accept);
    acceptStable.current = accept;
    useEffect(() => {
        return useDndStore.subscribe((state) => state.dragging, (draggedId) => {
            localEl?.setAttribute('data-dragging', draggedId ? 'true' : 'false');
            if (draggedId) {
                const data = draggableDataRegistry.get(draggedId);
                if (data) {
                    dndLogger.debug('Evaluating droppable accept with data', data);
                    const accepted = !acceptStable.current ||
                        acceptStable.current({
                            id: draggedId,
                            data,
                        }, gesture);
                    localEl?.setAttribute('data-dragged-accepted', accepted ? 'true' : 'false');
                }
            }
            else {
                localEl?.removeAttribute('data-dragged-accepted');
            }
        }, {
            fireImmediately: true,
        });
    }, [localEl]);
    const finalRef = useMergedRef(bindBounds, userRef, localRef);
    useEffect(() => droppableDataRegistry.register(id, data), [id, data]);
    const content = useRender({
        defaultTagName: svg ? 'g' : 'div',
        render,
        ref: disabled ? userRef : finalRef,
        props: rest,
        state: {
            role: 'droppable',
            droppableDisabled: disabled,
            overAccepted: isAcceptedOver,
            overRejected: isRejectedOver,
        },
        stateAttributesMapping: {
            role: () => ({ 'data-role': 'droppable' }),
            droppableDisabled: (value) => value ? { 'data-droppable-disabled': '' } : null,
            overAccepted: (value) => value === null
                ? null
                : { 'data-over-accepted': value ? 'true' : 'false' },
            overRejected: (value) => value === null
                ? null
                : { 'data-over-rejected': value ? 'true' : 'false' },
        },
    });
    if (noParenting) {
        return content;
    }
    return (_jsx(DroppableContext.Provider, { value: id, children: content }));
}
const DroppableContext = createContext(null);
export function useParentDroppable() {
    return useContext(DroppableContext);
}
//# sourceMappingURL=Droppable.js.map